import classNames from "classnames";
import React from "react";
import { TransitionGroup } from "react-transition-group";

import { RichtextModel } from "@courses-pm2/api";
import { Ribbon } from "@courses-pm2/ribbon-util-component";
import { asyncTimeout } from "@use/async-timeout";
import { autobind, throttleToAnimationFrame } from "@use/decorators";

import { colors } from "@courses-pm2/richtext-component/src/ts/config/colors";
import { easings } from "@courses-pm2/richtext-component/src/ts/config/easings";

import { TextFade } from "@courses-pm2/richtext-component/src/ts/components/transitions/TextFade";
import { getHeightRequiredToFitLines } from "../utils/get-height-required-to-fit-lines";
import { Caption } from "./Caption";

export type RichtextComponentProps = {
  /**
   * The model contains all of the content to render within each instance of
   * the component.
   */
  model: RichtextModel;
};

type RichtextComponentState = {
  /**
   * Specifies whether the component is currently collapsed
   */
  collapsed?: boolean;

  /**
   * Specifies an internal representation in px of the height of the component
   * when collapsed
   */
  collapsedHeight: number;

  /**
   * Specifies whether or not it makes sense to currently allow the component
   * to expand
   */
  componentCanExpand?: boolean;

  /**
   * Specifies whether or not we're currently transitioning
   */
  isTransitioning?: boolean;
};

/**
 * Specifies the desired amount of lines to be rendered when in a collapsed state
 */
const COLLAPSED_LINES = 8;

/**
 * Specifies the duration in ms it takes to transition from collapsed to expanded
 * and vice versa
 */
const TRANSITION_DURATION = 625;

/**
 * Root of the render tree for the standard image-component used to render
 * image elements hosted within PM2.
 */
export class RichtextComponent extends React.PureComponent<
  RichtextComponentProps,
  RichtextComponentState
> {
  /**
   * Render component as intially collapsed if it's collapsible
   */
  public state: RichtextComponentState = {
    collapsed: this.props.model.collapsible,
    componentCanExpand: this.props.model.collapsible,
    collapsedHeight: 0,
  };

  /**
   * Contains a reference to the root node of the component
   */
  protected readonly wrapperRef: React.RefObject<HTMLDivElement | null> =
    React.createRef();

  /**
   * Ensure that tables are prepared and the proper collapsed height is determined
   * on mount.
   */
  public componentDidMount(): void {
    if (this.props.model.collapsible) {
      this.updateCollapsedHeight();

      window.addEventListener("resize", this.onResize);
      window.addEventListener("orientationchange", this.onResize);
    }
  }

  /**
   * Ensure our richtext content is always up to date, in case new content was
   * passed via the model.
   */
  public componentDidUpdate(): void {
    if (this.props.model.collapsible) {
      this.updateCollapsedHeight();
    }
  }

  /**
   * Remove event listeners if required
   */
  public componentWillUnmount(): void {
    if (this.props.model.collapsible) {
      window.removeEventListener("resize", this.onResize);
      window.removeEventListener("orientationchange", this.onResize);
    }
  }

  /**
   * Primary render method.
   */
  public render(): React.JSX.Element {
    const { model } = this.props;

    const richtextClassName = classNames(
      "richtext",
      !!model.theme
        ? `richtext--theme--${model.theme.replace(/\ /g, "-")}`
        : "",
      {
        "richtext--withRibbon": !!model.ribbon,
        "richtext--autoCenter": !!model.autoCenter,
        "richtext--nested": !!model.nested,
        "richtext--scrambled": !!model.isScrambled,
        "richtext--solid": !!model.solid,
      },
    );

    return (
      <div className={richtextClassName}>
        {this.renderRibbon()}
        {this.renderContent()}
        {this.renderToggleButton()}

        {getGeneralStyling()}
        {getHeadingsStyling()}
        {getTableStyling()}
        {getPrintStyling()}

        <style jsx>{`
          .richtext {
            position: relative;
            margin: 0 0 30px;
            line-height: 1.4em;

            /**
                         * Apply padding and background around component if a ribbon
                         * is renderered alongside it
                         */
            &.richtext--withRibbon,
            &.richtext--solid {
              background: rgba(90, 60, 40, 0.08);
              padding: 40px 42px;
            }

            /**
                         * If autoCenter is enabled, then ensure content is
                         * vertically centered
                         */
            &.richtext--autoCenter {
              text-align: center;
            }

            /**
                         * The root richtext elements should have applied a
                         * max-width as per spec.
                         */
            &:not(.richtext--nested) {
              max-width: 40em;
              margin-left: auto;
              margin-right: auto;
            }

            /** Apply default styling for boks-2 theme */
            &.richtext--theme--boks-2 {
              padding: 40px 42px;
              background: rgba(90, 60, 40, 0.08);
              border: 1px #343434 dashed;
            }

            /** Apply default styling for the boks-3 theme */
            &.richtext--theme--boks-3 {
              padding: 40px 42px;
              background: rgba(90, 60, 40, 0.08);
              border: 1px #343434 solid;
            }
          }

          @media (max-width: 768px) {
            /**
                         * Slightly reduce padding on richtext elements that
                         * have ribbons.
                         */
            .richtext.richtext--withRibbon {
              padding: 30px;
            }
          }
        `}</style>
      </div>
    );
  }

  /**
   * Helper that'll render the <Ribbon /> utility to inject the icon on the
   * top-left side of the component.
   */
  protected renderRibbon(): React.JSX.Element | undefined {
    if (!this.props.model.ribbon) {
      return undefined;
    }

    return <Ribbon iconLetter={this.props.model.ribbon} />;
  }

  /**
   * Renderer that'll render the actual content of the component
   */
  protected renderContent(): React.JSX.Element {
    const { model } = this.props;

    const className = classNames("richtext__wrapper", {
      "richtext__wrapper--canExpand":
        model.collapsible && this.state.componentCanExpand,
      "richtext__wrapper--collapsed": this.state.collapsed,
      "richtext__wrapper--transitioning": this.state.isTransitioning,
      isSafari: isPlatformSafari(),
    });

    return (
      <div className={className} ref={this.wrapperRef}>
        <Caption model={this.props.model} />

        <div className="richtext__content">{this.props.model.render()}</div>

        <style jsx>{`
          .richtext__wrapper {
            /** Allow usage of position absolute within */
            position: relative;

            /** Attempt to ensure that long words won't overflow container on mobile */
            word-break: break-word;

            /** Handle scroll anchoring correctly when collapsing */
            overflow-anchor: none;

            /** Only apply transition while tranistioning */
            &.richtext__wrapper--transitioning {
              transition: height ${TRANSITION_DURATION}ms ${easings.inOutQuart};
            }

            /**
                        * If the component can expand, prepare rules to allow
                        * usage of line-clamp
                        */
            &.richtext__wrapper--canExpand {
              /** Hide overflowing content */
              word-wrap: break-word;
              text-overflow: ellipsis;
              overflow: hidden;

              &:not(.isSafari) {
                display: -webkit-box;
                -webkit-box-orient: vertical;
              }

              &.isSafari.richtext__wrapper--collapsed {
                /**
                * Force height such that Safari may render the correct amount of
                * lines (as of 15.3 Safari does not support line-clamping with block
                * children)
                */
                height: ${this.state.collapsedHeight}px;
              }
            }

            /**
            * If collapsed, force height to match desired amount of
            * collapsed lines.
            */
            &:not(.isSafari).richtext__wrapper--collapsed:not(
                .richtext__wrapper--transitioning
              ) {
              -webkit-line-clamp: ${COLLAPSED_LINES};
              -moz-line-clamp: ${COLLAPSED_LINES};
              line-clamp: ${COLLAPSED_LINES};
            }
          }
        `}</style>
      </div>
    );
  }

  /**
   * Renderer that'll inject the toggle (expand / collapse) button which will
   * be displayed if the richtext component has collapsible content.
   */
  // tslint:disable-next-line max-func-body-length
  protected renderToggleButton(): React.ReactNode {
    // Don't render the expand button if the component shouldn't be able
    // to expand.
    if (!this.props.model.collapsible || !this.state.componentCanExpand) {
      return;
    }

    const iconClassName = classNames("richtext__toggler__icon", {
      "richtext__toggler__icon--collapsed": this.state.collapsed,
      "richtext__toggler__icon--expanded": !this.state.collapsed,
    });

    return (
      <span
        className="richtext__toggler"
        onClick={this.onToggleClick}
        role="button"
      >
        <i className={iconClassName} />

        <span className="richtext__toggler__text">
          <TransitionGroup>
            <TextFade key={`expandButtonText-${!!this.state.collapsed}`}>
              {this.state.collapsed ? "Læs mere" : "Læs mindre"}
            </TextFade>
          </TransitionGroup>
        </span>

        <style jsx>{`
          .richtext__toggler {
            /**
                         * Display children alongside each other, centered
                         * vertically
                         */
            display: inline-flex;
            align-items: center;

            /** Setup spacing between button and content */
            margin-top: 20px;

            /** Prepare transitions */
            transition: color 0.1s linear;

            /** Indicate that element is clickable */
            cursor: pointer;

            /** Use default highlight color on hover */
            &:hover {
              color: ${colors.orange};

              & .richtext__toggler__icon {
                border-color: ${colors.orange};

                &::before,
                &::after {
                  background-color: ${colors.orange};
                }
              }
            }

            & .richtext__toggler__icon {
              /** Allow usage of position:absolute within */
              position: relative;

              /** Setup icon dimensions */
              display: block;
              box-sizing: border-box;
              width: 24px;
              height: 24px;

              /** Setup spacing between icon and text */
              margin-right: 7px;

              /** Render border around icon */
              border: 2px solid ${colors.black};
              border-radius: 50%;

              /** Prepare hover transition */
              transition: border-color 0.1s linear;

              /**
                             * Render the lines of the icon using pseudo-
                             * elements.
                             */
              &::before,
              &::after {
                /** Force rendering */
                content: "";

                /** Position in middle of icon */
                position: absolute;
                left: calc(50% - 6px);
                top: calc(50% - 1px);

                /** Setup dimensions of line */
                height: 2px;
                width: 12px;

                /** Render the line */
                background-color: ${colors.black};

                /** Prepare transitions */
                transition: transform ${TRANSITION_DURATION}ms
                    ${easings.inOutQuart},
                  background-color 0.1s linear;
              }

              &.richtext__toggler__icon--collapsed::before {
                transform: rotate(0deg);
              }

              &.richtext__toggler__icon--collapsed::after {
                /** Rotate one line 90deg in order to make a plus */
                transform: rotate(90deg);
              }

              /** When expanded, rotate arrows so that they align into a minus */
              &.richtext__toggler__icon--expanded::before,
              &.richtext__toggler__icon--expanded::after {
                transform: rotate(540deg);
              }
            }
          }

          .richtext__toggler__text {
            /**
                         * Allow usage of position: absolute within for
                         * transition.
                         */
            position: relative;

            /**
                         * Ensure text doesn't break into multiple lines during
                         * transition.
                         */
            width: max-content;
            min-width: 200px;

            /** Setup font */
            font-weight: 700;
          }
        `}</style>
      </span>
    );
  }

  /**
   * Listener that's triggered when the user clicks the button to toggle (ie.
   * expand / collapse) the content.
   */
  protected onToggleClick = () => {
    const { model } = this.props;
    const content = this.wrapperRef.current;

    // If we shouldn't be able to collapse/expand, or if we're currently
    // transitioning, then bail out...
    if (!model.collapsible || !content || this.state.isTransitioning) {
      return;
    }

    // Force initial height to allow transition to run
    content.style.height = this.state.collapsed
      ? `${this.state.collapsedHeight}px`
      : `${content.clientHeight}px`;

    // Allow the DOM to be prepared for a transition, before it's actually
    // going to be triggered...
    this.setState({ isTransitioning: true }, async () => {
      // Now determine the new desired height of the container, based on
      // the current state
      const desiredHeight = this.state.collapsed
        ? content.scrollHeight
        : this.state.collapsedHeight;

      // Triggers the CSS-transition
      content.style.height = `${desiredHeight}px`;

      // Update internal state, so that the React component itself knows
      // what's up!

      this.setState({ collapsed: !this.state.collapsed });

      // Wait for the transition to finish, and then reset the hardcoded
      // height - this will allow the component height to change
      // responsively if window size is changed
      await asyncTimeout(TRANSITION_DURATION);

      // Register that the transition has ended, which will allow the user
      // to trigger a new event
      this.setState({ isTransitioning: false }, () => {
        content.style.height = "";
      });
    });
  };

  /**
   * Method that'll update the internal representation of the collapsed height
   * to match the actual DOM collapsed height
   */
  protected updateCollapsedHeight = () => {
    const content = this.wrapperRef.current;

    // Bail out if the component isn't properly mounted
    if (!content) {
      return;
    }

    // First of all, force cropping after the desired number of lines!
    content.style.setProperty("line-clamp", `${COLLAPSED_LINES}`);
    content.style.setProperty("-webkit-line-clamp", `${COLLAPSED_LINES}`);
    content.style.setProperty("-moz-line-clamp", `${COLLAPSED_LINES}`);
    content.style.setProperty("min-height", "unset");

    // Calculate the height now that the number of lines have been cropped!
    let collapsedHeight = content.clientHeight;

    // Safari fails to recognize line-clamping on elements that have block
    // children inside. In this case .clientHeight will compute to 0 when
    // line-height is set to a low number and .scrollHeight when line-clamping is
    // set to a VERY large number.
    //
    // That is not what we want.. This method should return the actual height of
    // the content element when it is cropped to the collapsed amount of lines,
    // and thus if any of the two conditions is true, then we must manually
    // extract the required height to fit exactly the desired amount of lines.
    if (
      collapsedHeight === 0 ||
      isPlatformSafari() ||
      content.clientHeight === content.scrollHeight
    ) {
      collapsedHeight = getHeightRequiredToFitLines(content, COLLAPSED_LINES);
    }

    // Finally re-allow the content to contain whatever amount of text it is
    // supposed to
    content.style.setProperty("line-clamp", "");
    content.style.setProperty("-webkit-line-clamp", "");
    content.style.setProperty("-moz-line-clamp", "");
    content.style.setProperty("min-height", "");

    // Now that we've determined the collapsed height, determine if the
    // component should be able to expand
    this.setState({
      collapsedHeight,
      componentCanExpand: this.canComponentExpand(collapsedHeight),
    });
  };

  /**
   * Helper that determines whether the component can currently expand (ie. if
   * there is more content available than can be displayed before the fold).
   */
  protected canComponentExpand = (collapsedHeight?: number): boolean => {
    const { model } = this.props;
    const content = this.wrapperRef.current;

    // If we're running a transition, then we can ALWAYS collapse the
    // element (we're doing it right now)
    if (this.state.isTransitioning) {
      return true;
    }

    // If the model doesn't specify that the component is collapsible, then
    // it should never expand/collapse.
    if (!model.collapsible || !content) {
      return false;
    }

    const expandedHeight = content.scrollHeight;

    // The component should only be able to expand if its content is larger
    // than the collapsed height of the component
    return expandedHeight > (collapsedHeight || this.state.collapsedHeight);
  };

  /**
   * Method that'll be triggered on resize in order to ensure we have the
   * currently correct collapsed height stored.
   */
  @autobind()
  @throttleToAnimationFrame()
  protected onResize(): void {
    this.updateCollapsedHeight();
  }
}

/**
 * Renderer that'll inject a stylesheet with general styles used within the
 * richtext component
 */
// tslint:disable-next-line max-func-body-length
function getGeneralStyling(): React.JSX.Element {
  return (
    <style jsx global>{`
      .richtext__content {
        /**
                 * Setup general styling related to paragraphs within the
                 * richtext component.
                 */
        & p {
          margin: 20px 0;

          &:last-child {
            margin-bottom: 0;
          }

          & > :first-child {
            margin-top: 0;
          }

          & > :last-child {
            margin-bottom: 0;
          }

          /** Remove margins alltogether from empty paragraphs */
          &:empty {
            display: none;
          }

          /** Hide line breaks at the top / bottom of the paragraph */
          & > br:first-child,
          & > br:last-child {
            display: none;
          }

          /** Hide line breaks following immediately after */
          & + br {
            display: none;
          }
        }

        & > :first-child {
          margin-top: 0;
        }

        & > :last-child {
          margin-bottom: 0;
        }

        /**
                 * Setup styles related to ensure proper margins when using
                 * line-breaks within the feed.
                 */
        & br + .accordion,
        & br + .audio,
        & br + .grid,
        & br + .iframe,
        & br + .image,
        & br + .richtext,
        & br + .slider,
        & br + .video {
          /**
                     * Decrease margin of components following right after a
                     * <br />-tag!
                     */
          margin-top: calc(30px - 1.4em);
        }

        & br + p {
          /**
                     * Adjust margins of paragraphs following immediately after
                     * a <br />-tag.
                     */
          margin-top: calc(20px - 1.4em);
        }

        /**
                 * Setup styles of list elements nested within the component.
                 */
        & ul,
        & ol {
          margin: 20px 0;
          padding: 0 0 0 20px;

          /**
                     * Adjust margins of following elements if there's an
                     * accidental <br /> tag between.
                     */
          & + br {
            & + .accordion,
            & + .audio,
            & + .grid,
            & + .iframe,
            & + .image,
            & + .richtext,
            & + .slider,
            & + .video {
              /**
                             * Decrease margin of components following right
                             * after.
                             */
              margin-top: calc(10px - 1.4em);
            }

            & + p {
              /** Reset margins of paragraphs all together! */
              margin-top: 0;
            }
          }
        }

        /**
                 * Set up styling of custom components nested within the
                 * richtext grid.
                 */
        & .accordion,
        & .audio,
        & .grid,
        & .iframe,
        & .image,
        & .richtext,
        & .slider,
        & .video {
          margin-top: 30px;
          margin-bottom: 30px;

          & + br {
            /**
                         * Hide line breaks following immediately after a custom
                         * component.
                         */
            display: none;
          }
        }

        /** Reset margins for components embedded within tables */
        & table {
          & .accordion,
          & .audio,
          & .grid,
          & .iframe,
          & .image,
          & .richtext,
          & .slider,
          & .video {
            &:first-child {
              margin-top: 0;
            }

            &:last-child {
              margin-bottom: 0;
            }
          }
        }

        /**
                 * Apply default link styling within.
                 */
        & a {
          /** Reset link styling */
          color: #dc4320;
          text-decoration: none;

          /** Apply hover transition */
          transition: color 0.1s linear;

          &:hover {
            color: #bb381b;
          }
        }

        /**
                 * Reset margins on components nested directly on top / bottom
                 * of the richtext component.
                 */
        & > .accordion,
        & > .audio,
        & > .grid,
        & > .iframe,
        & > .image,
        & > .richtext,
        & > .slider,
        & > .video {
          &:first-child {
            margin-top: 0;
          }

          &:last-child {
            margin-bottom: 0;
          }
        }
      }

      .richtext--scrambled .richtext__content span {
        background-color: transparent !important;
        background-image: url("data:image/svg+xml,%3Csvg width='1' height='40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg%3E%3Crect id='scramble-bg' height='20' width='1' y='10' x='0' stroke-width='1.5' stroke='%23d6d6d6' fill='%23fff'/%3E%3C/g%3E%3C/svg%3E%0A") !important;
        background-size: contain !important;
        background-repeat: repeat-x !important;
        color: rgba(0, 0, 0, 0) !important;
        user-select: none !important;
      }
    `}</style>
  );
}

/**
 * Setup all styles related to headings
 */
function getHeadingsStyling(): React.JSX.Element {
  return (
    <style jsx global>{`
      .richtext__content {
        & h1,
        & h2,
        & h3,
        & h4,
        & h5,
        & h6 {
          /** Override defaults */
          margin: 0;
          padding: 0;
        }

        /** Setup default sizing */
        & h1 {
          font-weight: 400;
          font-size: 2.25em;
          line-height: 1.1em;
          margin-left: -1px;
          margin-top: 15px;
          margin-bottom: 15px;
        }

        & h2 {
          font-weight: 700;
          font-size: 1.75em;
          line-height: 1.2em;
          margin-top: 15px;
          margin-bottom: 15px;
        }

        & h3 {
          font-weight: 400;
          font-size: 1.75em;
          line-height: 1.2em;
          margin-top: 15px;
          margin-bottom: 15px;
        }

        & h4 {
          font-weight: 400;
          font-size: 1.25em;
          line-height: 1.3em;
          margin-top: 5px;
          margin-bottom: 5px;
        }

        & h5 {
          font-weight: 400;
          font-size: 1em;
          line-height: 1.4em;
        }

        & h6 {
          font-weight: 400;
          font-size: 0.875em;
          line-height: 1.4em;
        }

        /** Reset margins on first and last elements */
        & > h1,
        & > h2,
        & > h3,
        & > h4,
        & > h5,
        & > h6 {
          &:first-child {
            margin-top: 0;
          }

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    `}</style>
  );
}

/**
 * Setup all styles related to tables rendered inside richtext components
 */
// tslint:disable-next-line max-func-body-length
function getTableStyling(): React.JSX.Element {
  return (
    <style jsx global>{`
      .richtext__content {
        & .richtext__table {
          width: 100%;
          margin: 20px 0;

          /**
                     * Ensure overflow of tables can be scrolled instead of
                     * actually overflowing
                     */
          overflow: auto;
          -webkit-overflow-scrolling: touch;
        }

        & .richtext__table--bordered {
          /** Render a border around bordered tables */
          border: 2px solid ${colors.black};
        }

        /** Setup rules for styling for tables */
        & table {
          /** Reset default styling */
          border-collapse: collapse;
          table-layout: fixed;
          border: 0;

          /** Always fill up horizontally */
          min-width: 100%;

          & tbody {
            & th.multipleRows,
            & td.multipleRows {
              /**
                             * Disable background color on cells spanning
                             * multiple rows (this could result in bad rendering
                             * if rows have alternate styling).
                             */
              background-color: transparent !important;
            }
          }

          /** Setup styles for striped tables */
          &.table-striped {
            /**
                         * Force padding on striped table, since
                         * we know solid backgrounds will be applied here.
                         */
            & td,
            & th {
              padding-left: 10px;
              padding-right: 10px;
            }

            /** Ensure stripes are rendered on striped tables */
            & tbody tr:nth-child(odd) {
              & th,
              & td {
                background-color: rgba(56, 56, 56, 0.1);
              }
            }
          }

          /** Setup styles for bordered tables */
          &.table-bordered {
            /**
                         * Force padding around bordered tables, since we know
                         * borders will be around cells.
                         */
            & th,
            & td {
              padding-left: 10px;
              padding-right: 10px;

              /** Render internal borders as well */
              border: 1px solid ${colors.black};
            }

            /** Disable top / bottom borders */
            & tr:first-child {
              & td,
              & th {
                border-top-width: 0;
              }
            }

            & tr:last-child {
              & td,
              & th {
                border-bottom-width: 0;
              }
            }

            /** Disable left / right borders */
            & td:first-child,
            & th:first-child {
              border-left-width: 0;
            }

            & td:last-child,
            & th:last-child {
              border-right-width: 0;
            }
          }

          /** Setup styles related to tables that can be hovered */
          &.table-hover {
            padding-left: 10px;
            padding-right: 10px;

            /** Apply bg on table body rows when hovered */
            & tbody tr:hover {
              & td {
                background: rgba(56, 56, 56, 0.15);
              }
            }
          }
        }

        /** Setup general styling for all cells */
        & td,
        & th {
          /** Override defaults */
          border: 0;

          /** Apply basic padding */
          padding: 10px;
          vertical-align: top;

          /**
                     * First child should align with other text outside
                     * table if no borders or bg-colors are applied.
                     */
          &:first-child {
            padding-left: 0;
          }

          /** ... same goes for last-child */
          &:last-child {
            padding-right: 0;
          }

          & p {
            /** Never render margins of paragraphs inside tables */
            margin: 0;
          }
        }

        /** Setup styling related to header elements only */
        & th {
          /** Highlight title elements */
          font-weight: 700;
          border-bottom: 2px solid ${colors.black};
          text-align: left;
        }

        /** Clear margins when first and last child is a div */
        & > .richtext__table {
          &:first-child {
            margin-top: 0;
          }

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    `}</style>
  );
}

/**
 * Method that'll inject styles required for printing richtext components
 */
function getPrintStyling(): React.JSX.Element {
  return (
    <style jsx global>{`
      @media print {
        .richtext {
          font-family: "Times New Roman", sans-serif, figtree;

          /** Improve print colors */
          background: transparent;
          color: black !important;

          /** Use available space */
          width: 100% !important;

          /** Force expanded state when printed */
          & .richtext__content {
            -webkit-line-clamp: initial !important;
            -moz-line-clamp: initial !important;
          }

          /** Remove the toggler button from print styles */
          & .richtext__toggler {
            display: none !important;
          }

          /** hide richtext ribbon */
          & > .ribbon {
            display: none;
          }
        }
      }
    `}</style>
  );
}

function isPlatformSafari(): boolean {
  return (
    typeof navigator !== "undefined" &&
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  );
}
